import {memo, useDeferredValue, useMemo, useLayoutEffect} from 'react';
import type {NativeSessionDetail} from './lib/types';
import type {NativeChatActivity, NativeChatTurnActivity, NativeAgentView} from '../src/native-hermes-types';
import {ChatMarkdown} from './chat-markdown';
import {conversationMessages, type ConversationMessage} from './lib/conversation-messages';
import {ChatTurnActivity, chatCurrentWork} from './chat-activity';
import {ChatFeedbackProvider, NativeMessageActions} from './chat-message-actions';
import type {NativeCommunicationReceipt, NativeCommunicationStream} from '../src/native-communication';
import {ErrorNotice} from './components';
import {TEAM_ROLES} from '../src/team';
import type {NativeTeamProjection} from './team-workspace';

const messageTime = new Intl.DateTimeFormat(undefined, {weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});

type TranscriptProps = {
  accountId: string | null;
  history: readonly Record<string, unknown>[]; liveTurn?: NativeSessionDetail['liveTurn']; agentName: string;
  messagePrefix: string; activity: NativeChatActivity | undefined; connected: boolean; nativeSessionId: string; sessionId: string;
  agents: NativeAgentView[]; onSelectAgent: (id: string | null) => void;
  projection: NativeTeamProjection;
  acceptedRequestId?: string | undefined;
  communicationStreams?:readonly NativeCommunicationStream[]|undefined;
  communicationFailures?:readonly NativeCommunicationReceipt[]|undefined;
  onRendered?: (history: readonly Record<string, unknown>[]) => void;
};

// Draft edits must not project or render the recorded conversation again. Keep
// feedback inside this boundary so typing cannot invalidate every reply action.
export const NativeTranscript = memo(function NativeTranscript(props: TranscriptProps) {
  // Never retain the previous conversation while a selected chat/agent loads.
  return <DeferredTranscript key={JSON.stringify([props.accountId, props.sessionId, props.nativeSessionId])} {...props}/>;
});

function DeferredTranscript({history, liveTurn = null, activity, agents, projection, acceptedRequestId, communicationStreams, communicationFailures, ...immediate}: TranscriptProps) {
  const snapshot = useMemo(() => ({history, liveTurn, activity, agents, projection, acceptedRequestId, communicationStreams, communicationFailures}), [history, liveTurn, activity, agents, projection, acceptedRequestId, communicationStreams, communicationFailures]);
  // External-store publications are synchronous. Reconcile their potentially
  // long transcript at interruptible priority, leaving input and admission state
  // immediate. Connection changes still freeze native timers in the same commit.
  const deferred = useDeferredValue(snapshot);
  // A just-acknowledged native message is user input, not background work. Show
  // that receipt immediately, then defer ordinary updates from the same turn.
  const presentation = deferred.acceptedRequestId === acceptedRequestId ? deferred : snapshot;
  useLayoutEffect(() => {immediate.onRendered?.(presentation.history);}, [presentation.history, immediate.onRendered]);
  return <TranscriptContent {...presentation} {...immediate}/>;
}

const TranscriptMessage = memo(function TranscriptMessage({role, text, imageAttachmentCount, attachments, timestamp,
  unprocessedCorrection, agentName, showDate, accountId}: Pick<ConversationMessage,
    'role' | 'text' | 'imageAttachmentCount' | 'attachments' | 'timestamp' | 'unprocessedCorrection'> & {agentName: string | undefined; showDate: boolean; accountId: string | null}) {
  const date = timestamp === null || !showDate ? null : new Date(timestamp * 1000);
  return <article className={role === 'user' ? 'user-message' : 'agent-response'}
    aria-label={role === 'user' ? 'Your message' : agentName ? `${agentName} reply` : 'Assistant reply'}>
    {date && <time className="chat-message-time" dateTime={date.toISOString()}>{messageTime.format(date)}</time>}
    <span className="visually-hidden">{role === 'user' ? 'You' : agentName ?? 'Assistant'}</span>
    {text && (role === 'user' ? <p>{text}</p> : <ChatMarkdown text={text} accountId={accountId}/>)}
    {imageAttachmentCount > 0 && <span className="chat-image-attachment">{imageAttachmentCount === 1 ? 'Image attached' : `${imageAttachmentCount} images attached`}</span>}
    {attachments.length>0&&<ul className="chat-file-attachments" aria-label="Attached files">{attachments.map(file=><li key={file.id}>{file.name}</li>)}</ul>}
    {unprocessedCorrection && <p className="field-help">This message was not processed during that turn.</p>}
  </article>;
});

const TranscriptContent = memo(function TranscriptContent({history, liveTurn = null, agentName, messagePrefix, activity,
  connected, nativeSessionId, sessionId, accountId, agents, onSelectAgent, projection, communicationStreams, communicationFailures}: TranscriptProps) {
  const messages = useMemo(() => conversationMessages(history, messagePrefix, liveTurn, communicationStreams), [history, messagePrefix, liveTurn, communicationStreams]);
  const {requestErrors, otherErrors} = useMemo(() => {
    const loadedInputs = new Set(messages.filter(message => message.role === 'user').map(message => message.messageKey));
    const requestErrors = new Map<string, NativeCommunicationReceipt[]>();
    const otherErrors: NativeCommunicationReceipt[] = [];
    for (const receipt of communicationFailures ?? []) {
      if (!receipt.error) continue;
      const key = `logical:${receipt.logical_id}`;
      if (!loadedInputs.has(key)) {otherErrors.push(receipt); continue;}
      const errors = requestErrors.get(key) ?? [];
      errors.push(receipt); requestErrors.set(key, errors);
    }
    return {requestErrors, otherErrors};
  }, [messages, communicationFailures]);
  const {starts, running, unanchoredRunning} = useMemo(() => {
    // Index the native user identities and next normal prompt once. Correction
    // messages stay inside their original turn, just as in Hermes's projection.
    const userRows = new Map<number, number>();
    const nextPrompts = new Array<number>(messages.length);
    let nextPrompt = -1;
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index]!;
      nextPrompts[index] = nextPrompt;
      if (message.role === 'user') {
        if (message.rowId !== null) userRows.set(message.rowId, index);
        if (!message.isCorrection) nextPrompt = index;
      }
    }
    const starts = new Map<number, NativeChatTurnActivity[]>();
    const anchoredIds = new Set<string>();
    for (const turn of activity?.turns ?? []) {
      const index = turn.userRowId === null ? undefined : userRows.get(turn.userRowId);
      if (index === undefined) continue;
      const opening = starts.get(index) ?? [];
      opening.push(turn); starts.set(index, opening);
      anchoredIds.add(turn.id);
    }
    const runningEdges = new Array<number>(messages.length + 1).fill(0);
    for (let index = 0; index < messages.length; index++) {
      const opening = starts.get(index);
      if (!opening) continue;
      const next = nextPrompts[index]!;
      const end = next === -1 ? messages.length - 1 : next - 1;
      for (const turn of opening) {
        if (turn.status === 'running' && end > index) {
          runningEdges[index + 1]!++;
          runningEdges[end + 1]!--;
        }
      }
    }
    let active = 0;
    const running = messages.map((_, index) => {active += runningEdges[index]!; return active > 0;});
    const unanchoredRunning = activity?.turns.filter(turn => turn.status === 'running' && !anchoredIds.has(turn.id)) ?? [];
    return {starts, running, unanchoredRunning};
  }, [messages, activity?.turns]);
  const dayStarts = useMemo(() => {
    let previousDay: string | null = null;
    return messages.map(message => {
      const date = message.timestamp === null ? null : new Date(message.timestamp * 1000);
      if (date && !Number.isFinite(date.getTime())) throw new Error(`Invalid message timestamp: ${message.timestamp}`);
      const day = date?.toDateString() ?? null;
      const showDate = day !== null && day !== previousDay;
      if (day !== null) previousDay = day;
      return showDate;
    });
  }, [messages]);
  const currentTurn = activity?.turns.filter(turn => turn.status === 'running').at(-1);
  const ownProjection = projection.status === 'ready' && projection.sessionId === sessionId;
  const currentWork = ownProjection ? chatCurrentWork(projection, nativeSessionId) : null;
  const workDetails = (turn: NativeChatTurnActivity) => <ChatTurnActivity key={turn.id} turn={turn}
    observedAt={activity!.observedAt} connected={connected} agents={agents}
    {...(connected && currentWork && currentTurn?.id === turn.id ? {currentWork} : {})}
    onSelectAgent={onSelectAgent} messagePrefix={messagePrefix}/>;
  return <ChatFeedbackProvider sessionId={sessionId}><div className="chat-history native-transcript" aria-label="Recorded conversation">
    {otherErrors.length > 0 && <details className="chat-previous-failure">
      <summary>Other saved request errors ({otherErrors.length})</summary>
      <p>These requests are outside the messages shown here.</p>
      {otherErrors.map(receipt => <RequestError key={receipt.request_id} receipt={receipt}/>)}
    </details>}
    {messages.length === 0 && !liveTurn && !unanchoredRunning.length && <div className="chat-start-heading"><h1>What would you like to work on?</h1></div>}
    {messages.map((message, index) => {
      const start = starts.get(index);
      const inProgress = running[index] || unanchoredRunning.length > 0 && index === messages.length - 1;
      const replyAgent=message.workReplyAgentId===null?undefined:agents.find(agent=>agent.sessionId===message.workReplyAgentId);
      const replyName=message.workReplyAgentId===null?agentName:TEAM_ROLES.find(role=>role.id===replyAgent?.roleId)?.name;
      const errors = message.role === 'user' && message.messageKey !== null ? requestErrors.get(message.messageKey) : undefined;
      return <div key={message.key} className="chat-turn-block" data-message-key={message.key}>
        <TranscriptMessage role={message.role} text={message.text} timestamp={message.timestamp}
          imageAttachmentCount={message.imageAttachmentCount} attachments={message.attachments} unprocessedCorrection={message.unprocessedCorrection}
          agentName={replyName} showDate={dayStarts[index]! || errors !== undefined} accountId={accountId}/>
        {errors?.map(receipt => <RequestError key={receipt.request_id} receipt={receipt}/>)}
        {message.incomplete&&<p className="field-help">This reply was interrupted.</p>}
        {start?.map(turn => workDetails(turn))}
        {message.role === 'assistant' && !inProgress && !message.streaming && <NativeMessageActions nativeSessionId={message.workReplyAgentId??(message.communicationRecord?sessionId:nativeSessionId)} messageRowId={message.rowId} {...(message.messageKey === null ? {} : {messageKey: message.messageKey})}
          text={message.text} timestamp={message.timestamp}/>}
      </div>;
    })}
    {unanchoredRunning.length > 0 && <section className="chat-unanchored-work" aria-label="Current work"><p>Current work</p>{unanchoredRunning.map(workDetails)}</section>}
  </div></ChatFeedbackProvider>;
});

function RequestError({receipt}: {receipt: NativeCommunicationReceipt}) {
  return <div className="chat-request-error" data-request-id={receipt.request_id}>
    <ErrorNotice title="There was a problem with this message"
      error={receipt.error ? {name: 'CommunicationError', message: receipt.error.message,
        ...(receipt.error.stack === undefined ? {} : {stack: receipt.error.stack}),
        cause: {requestId: receipt.request_id, status: receipt.status}} : null}/>
  </div>;
}
